import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { cn } from '@shared/lib/utils';

interface InvoiceQrProps {
  value: string;
  className?: string;
  size?: number;
}

/** Display size for invoice payment QR (Figma). */
export const INVOICE_QR_SIZE = 140;

/**
 * Backend QR images include a quiet zone. We slightly zoom to crop excess padding.
 * Local/mock QR must use the same quiet-zone margin so zoom looks identical.
 */
export const QR_ZOOM = 1.1;
/** Quiet-zone modules for locally generated QR (matches padded backend assets). */
const QR_MARGIN_MODULES = 2;

export function InvoiceQr({ value, className, size = INVOICE_QR_SIZE }: InvoiceQrProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void QRCode.toDataURL(value, {
      errorCorrectionLevel: 'M',
      margin: QR_MARGIN_MODULES,
      width: size * 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    }).then((url) => {
      if (!cancelled) setDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [size, value]);

  return (
    <div
      role="img"
      aria-label={value}
      className={cn('shrink-0 overflow-hidden rounded-md bg-white', className)}
      style={{ width: size, height: size }}
    >
      {dataUrl ? (
        <img
          src={dataUrl}
          alt=""
          className="h-full w-full object-cover"
          style={{ transform: `scale(${QR_ZOOM})` }}
        />
      ) : (
        <div className="h-full w-full animate-pulse rounded-lg bg-muted" />
      )}
    </div>
  );
}

export function InvoiceQrImage({
  src,
  address,
  size = INVOICE_QR_SIZE,
  className,
}: {
  src: string;
  address: string;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={cn('shrink-0 overflow-hidden rounded-md bg-white', className)}
      style={{ width: size, height: size }}
    >
      <img
        src={src}
        alt=""
        role="img"
        aria-label={address}
        className="h-full w-full object-cover"
        style={{ transform: `scale(${QR_ZOOM})` }}
      />
    </div>
  );
}
